import { getActiveDevPlanOverride, getActiveDevScenarioEntries } from './browser-state';
import {
    applyLegacyPlanSiteLimits,
    createLegacyCurrentPrice,
    isLegacyPlanOverride,
} from './legacy-plan-profiles';

const DEV_TRIAL_PRICE_ID = 'dev-plan-override-trial';

const NEUTRAL_SITE_STATE = {
    currentSiteLimits: {
        staff: { total: 1, limit: null, exceeded: false },
        newsletters: { total: 1, limit: null, exceeded: false },
        limitAutomations: { total: 0, limit: null, exceeded: false },
        members: { total: 500, limit: null, exceeded: false },
        customThemes: { active: 'source', hasOfficialTheme: true, limit: null, exceeded: false },
        customIntegrations: { active: false, limit: null, exceeded: false },
        limitAnalytics: { active: false, limit: true, exceeded: false },
        limitStripeConnect: { active: false, limit: true, exceeded: false },
    },
    currentSite: {
        subdirectory: null,
        sending_domain_status: null,
    },
};

const FLAG_SITE_OVERRIDES = {
    'custom-theme': {
        currentSiteLimits: {
            customThemes: { active: 'my-custom-theme', hasOfficialTheme: false },
        },
    },
    analytics: {
        currentSiteLimits: { limitAnalytics: { active: true, limit: false } },
    },
    'stripe-connect': {
        currentSiteLimits: { limitStripeConnect: { active: true, limit: false } },
    },
    subdirectory: {
        currentSite: { subdirectory: 'blog' },
    },
    'sending-domain': {
        currentSite: { sending_domain_status: 'VERIFIED' },
    },
};

const NUMERIC_SITE_OVERRIDE_BUILDERS = {
    staff: (total) => ({ currentSiteLimits: { staff: { total } } }),
    newsletters: (total) => ({ currentSiteLimits: { newsletters: { total } } }),
    automations: (total) => ({ currentSiteLimits: { limitAutomations: { total } } }),
    members: (total) => ({ currentSiteLimits: { members: { total } } }),
};

/**
 * @returns {boolean}
 */
function isDevelopment() {
    return process.env.NODE_ENV === 'development';
}

/**
 * @param {object} price
 * @returns {number}
 */
function getMembersLimitValue(price) {
    const membersLimit = (price?.limits || []).find((limit) => limit.name === 'members');
    return typeof membersLimit?.value === 'number' ? membersLimit.value : Infinity;
}

/**
 * @param {object} result
 * @param {object} override
 * @returns {object}
 */
function mergeSiteOverride(result, override) {
    const next = { ...result };

    if (override.currentSiteLimits) {
        next.currentSiteLimits = { ...result.currentSiteLimits };
        Object.entries(override.currentSiteLimits).forEach(([limitName, limitOverride]) => {
            next.currentSiteLimits[limitName] = {
                ...result.currentSiteLimits[limitName],
                ...limitOverride,
            };
        });
    }
    if (override.currentSite) {
        next.currentSite = { ...result.currentSite, ...override.currentSite };
    }

    return next;
}

/**
 * @param {string} id
 * @param {number|null} value
 * @returns {object|undefined}
 */
function materializeLimitScenario(id, value) {
    switch (id) {
        case 'staff':
            return NUMERIC_SITE_OVERRIDE_BUILDERS.staff(value);
        case 'newsletters':
            return NUMERIC_SITE_OVERRIDE_BUILDERS.newsletters(value);
        case 'automations':
            return NUMERIC_SITE_OVERRIDE_BUILDERS.automations(value);
        case 'members':
            return NUMERIC_SITE_OVERRIDE_BUILDERS.members(value);
        default:
            return FLAG_SITE_OVERRIDES[id];
    }
}

/**
 * Applies the active browser-backed plan override at the plan selector's read
 * seam. Shared billing data remains untouched.
 *
 * @param {object|null} currentPrice
 * @param {object[]|null} availableProducts
 * @param {number} [currentMembers]
 * @returns {object|null}
 */
export function applyDevPlanOverride(currentPrice, availableProducts, currentMembers = 0) {
    if (!isDevelopment()) {
        return currentPrice;
    }

    const override = getActiveDevPlanOverride();
    if (!override) {
        return currentPrice;
    }

    if (override === 'trial') {
        return {
            cost: 0,
            billing_period: 'year',
            stripe_price_id: DEV_TRIAL_PRICE_ID,
            base_product: '',
            limits: [],
            features: [],
        };
    }

    if (isLegacyPlanOverride(override)) {
        return createLegacyCurrentPrice(currentPrice, override);
    }

    const product = (availableProducts || []).find(
        (item) => item?.name?.toLowerCase() === override,
    );
    if (!product) {
        return currentPrice;
    }

    const yearlyPricesByMembers = (product.prices || [])
        .filter((price) => price.billing_period === 'year')
        .sort((left, right) => getMembersLimitValue(left) - getMembersLimitValue(right));
    const overriddenPrice =
        yearlyPricesByMembers.find((price) => getMembersLimitValue(price) >= currentMembers) ||
        yearlyPricesByMembers[yearlyPricesByMembers.length - 1];

    return overriddenPrice ? { ...overriddenPrice, base_product: product.name } : currentPrice;
}

/**
 * Applies active browser-backed limit scenarios to a current-site response.
 * Legacy allowances form the baseline and explicit usage scenarios win.
 * The input is never mutated.
 *
 * @param {object} currentSiteResponse
 * @returns {object}
 */
export function applyDevLimitScenario(currentSiteResponse) {
    if (!isDevelopment() || !currentSiteResponse?.currentSiteLimits) {
        return currentSiteResponse;
    }

    const entries = getActiveDevScenarioEntries();
    const planOverride = getActiveDevPlanOverride();
    const hasLegacyPlanOverride = isLegacyPlanOverride(planOverride);
    if (!entries.length && !hasLegacyPlanOverride) {
        return currentSiteResponse;
    }

    const scenarioBaseline = entries.length
        ? {
              ...currentSiteResponse,
              currentSiteLimits: {
                  ...currentSiteResponse.currentSiteLimits,
                  ...NEUTRAL_SITE_STATE.currentSiteLimits,
              },
              currentSite: {
                  ...currentSiteResponse.currentSite,
                  ...NEUTRAL_SITE_STATE.currentSite,
              },
          }
        : currentSiteResponse;
    const planBaseline = applyLegacyPlanSiteLimits(scenarioBaseline, planOverride);

    return entries.reduce((result, { id, value }) => {
        const override = materializeLimitScenario(id, value);
        return override ? mergeSiteOverride(result, override) : result;
    }, planBaseline);
}
